'use client';

import { Button } from '@pickleballinc/ui/components/ui/button';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';

import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { formatTeamLeagueLabels } from '@/utils/helpers';

interface TeamLeagueDetailHeaderNavButtonsProps {
	teamLeagueId: string;
	hasPermission?: boolean;
	shouldHideDistrics?: boolean;
	shouldHideDivisions?: boolean;
	isSeasonActive: boolean;
	isSeasonBtnVisible?: boolean;
	seasonStandingExist?: boolean;
}

interface NavItem {
	label: string;
	href: string;
}

export const generateNavItems = (
	isSeasonActive: boolean,
	shouldHideDistrics?: boolean,
	shouldHideDivisions?: boolean,
	isSeasonBtnVisible?: boolean,
	seasonStandingExist?: boolean,
	districtPluralTitle: string = 'Districts'
): NavItem[] => {
	const commonNavItems: NavItem[] = [
		{ label: 'Schedule', href: '/gameday' },
		...(!shouldHideDivisions ? [{ label: 'Divisions', href: '/divisions' }] : []),
		{ label: 'Teams', href: '/teams' },
		{ label: 'Players', href: '/players' },
		...(!shouldHideDistrics ? [{ label: districtPluralTitle, href: '/districts' }] : []),
		...(isSeasonBtnVisible ? [{ label: 'Sponsors', href: '/sponsors' }] : [])
	];

	const DETAILS: NavItem = { label: 'Details', href: '/' };
	const STANDINGS: NavItem = { label: 'Standings', href: '/standings' };
	const WEATHER: NavItem = { label: 'Weather', href: '/weather' };

	// Case 1: season active + standings exist - Standings first, Details last
	if (isSeasonActive && seasonStandingExist) {
		return [STANDINGS, ...commonNavItems, DETAILS, WEATHER];
	}

	// Case 2: standings exist but season NOT active - Details first, Standings last
	if (!isSeasonActive && seasonStandingExist) {
		return [DETAILS, ...commonNavItems, STANDINGS, WEATHER];
	}

	// Case 3: standings do NOT exist - no Standings, Details first
	return [DETAILS, ...commonNavItems, WEATHER];
};

export const TeamLeagueDetailHeaderNavButtons = ({
	teamLeagueId,
	shouldHideDistrics,
	shouldHideDivisions,
	isSeasonActive,
	isSeasonBtnVisible = false,
	seasonStandingExist
}: TeamLeagueDetailHeaderNavButtonsProps) => {
	const currentPath = usePathname();
	const searchParams = useSearchParams();
	const segmentedCurrentPath = currentPath.split('/');
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	const districtPluralTitle = labels.districtPluralTitle;

	const filteredNavItems = generateNavItems(
		isSeasonActive,
		shouldHideDistrics,
		shouldHideDivisions,
		isSeasonBtnVisible,
		seasonStandingExist,
		districtPluralTitle
	);

	const season = `${searchParams.get('season') ? `?season=${searchParams.get('season')}` : ''}`;

	return (
		<div className="flex w-full justify-center bg-gray-soft py-4 rounded-b-lg">
			<div className="no-scrollbar flex gap-5 overflow-x-auto px-4 sm:px-8">
				{filteredNavItems.map(({ label, href }, index) => (
					<Link href={`/team-leagues/${teamLeagueId}${href}${season}`} key={`${label}_${index}`} className="text-nowrap">
						<Button
							className={`flex w-full flex-1 border !border-gray-300 px-4 py-2 font-semibold`}
							variant={
								(href !== '/' && currentPath.includes(href)) || (href === '/' && segmentedCurrentPath.length === 3)
									? 'default'
									: 'outline'
							}
						>
							{label}
						</Button>
					</Link>
				))}
			</div>
		</div>
	);
};
